// src/features/developer/hooks/useJwtTool.ts
import { useState, useEffect, useCallback, useMemo } from 'react';
import { SignJWT, jwtVerify, JWTVerifyResult, decodeJwt } from 'jose';
import { debounce } from 'lodash';
import { EXAMPLE_HEADER, EXAMPLE_PAYLOAD } from '../constants/jwtConstants';

export type JwtToolMode = 'decoder' | 'encoder';

// Helper to safely parse JSON
const safeJsonParse = (str: string): Record<string, any> | null => {
    try {
        const parsed = JSON.parse(str);
        if (typeof parsed === 'object' && parsed !== null) {
            return parsed;
        }
        return null;
    } catch (e) {
        return null;
    }
};

// Helper to pretty-format JSON
const prettyJsonStringify = (obj: any): string => {
    if (typeof obj !== 'object' || obj === null) return '';
    return JSON.stringify(obj, null, 2);
};

export const useJwtTool = () => {
    const [mode, setMode] = useState<JwtToolMode>('decoder');
    const [encodedJwt, setEncodedJwt] = useState('');
    const [header, setHeader] = useState('');
    const [payload, setPayload] = useState('');
    const [secret, setSecret] = useState('');
    const [isJwtFormatValid, setIsJwtFormatValid] = useState<boolean | null>(null);
    const [isSignatureValid, setIsSignatureValid] = useState<boolean | null>(null);
    const [verificationResult, setVerificationResult] = useState<JWTVerifyResult | null>(null);
    const [error, setError] = useState<string>('');

    const headerObj = useMemo(() => safeJsonParse(header), [header]);
    const payloadObj = useMemo(() => safeJsonParse(payload), [payload]);

    // Debounced function to verify JWT
    const debouncedVerify = useCallback(
        debounce(async (jwt: string, secretKey: string) => {
            if (!jwt) {
                setIsJwtFormatValid(null);
                setIsSignatureValid(null);
                setVerificationResult(null);
                setHeader('');
                setPayload('');
                setError('');
                return;
            }

            try {
                // Step 1: Basic JWT format validation and decoding
                const parts = jwt.split('.');
                if (parts.length !== 3) {
                    throw new Error('Invalid JWT format. It must have three parts separated by dots.');
                }

                // Use jose's decodeJwt to parse header and payload
                const decoded = decodeJwt(jwt);
                const decodedHeader = { alg: decoded?.alg || 'HS256', typ: decoded?.typ || 'JWT' };
                const decodedPayload = decoded;

                setHeader(prettyJsonStringify(decodedHeader));
                setPayload(prettyJsonStringify(decodedPayload));
                setIsJwtFormatValid(true);
                setError('');

                // Step 2: Signature verification if secret is provided
                if (secretKey) {
                    try {
                        const key = new TextEncoder().encode(secretKey);
                        const result = await jwtVerify(jwt, key);
                        setVerificationResult(result);
                        setIsSignatureValid(true);
                    } catch (verifyError) {
                        setIsSignatureValid(false);
                        setVerificationResult(null);
                    }
                } else {
                    setIsSignatureValid(null); // No secret provided
                }
            } catch (e: any) {
                setIsJwtFormatValid(false);
                setIsSignatureValid(null);
                setVerificationResult(null);
                setHeader('');
                setPayload('');
                setError(e.message || 'Failed to decode JWT.');
            }
        }, 300),
        []
    );

    // Debounced function to encode JWT
    const debouncedEncode = useCallback(
        debounce(async (h: any, p: any, s: string) => {
            if (!h || !p) {
                setEncodedJwt('');
                return;
            }
            try {
                const key = new TextEncoder().encode(s);
                const jwt = await new SignJWT(p)
                    .setProtectedHeader(h)
                    .sign(key);
                setEncodedJwt(jwt);
                setError('');
            } catch (e: any) {
                setEncodedJwt('');
                setError(`Encoding Error: ${e.message}`);
            }
        }, 300),
        []
    );

    // Effect for decoder mode
    useEffect(() => {
        if (mode === 'decoder') {
            debouncedVerify(encodedJwt, secret);
        }
    }, [encodedJwt, secret, mode, debouncedVerify]);

    // Effect for encoder mode
    useEffect(() => {
        if (mode === 'encoder') {
            const hObj = safeJsonParse(header);
            const pObj = safeJsonParse(payload);
            debouncedEncode(hObj, pObj, secret);
        }
    }, [header, payload, secret, mode, debouncedEncode]);

    // Handlers
    const handleEncodedJwtChange = (value: string | undefined) => {
        setEncodedJwt(value || '');
    };

    const handleHeaderChange = (value: string | undefined) => {
        setHeader(value || '');
    };

    const handlePayloadChange = (value: string | undefined) => {
        setPayload(value || '');
    };

    const handleSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSecret(e.target.value);
    };

    const generateExample = useCallback(async () => {
        const exampleSecret = 'your-256-bit-secret-key-that-is-long-enough';
        const exampleHeaderStr = prettyJsonStringify(EXAMPLE_HEADER);
        const examplePayloadStr = prettyJsonStringify(EXAMPLE_PAYLOAD);

        setSecret(exampleSecret);
        setHeader(exampleHeaderStr);
        setPayload(examplePayloadStr);

        try {
            const key = new TextEncoder().encode(exampleSecret);
            const jwt = await new SignJWT(EXAMPLE_PAYLOAD)
                .setProtectedHeader(EXAMPLE_HEADER)
                .sign(key);

            setEncodedJwt(jwt);
            setError('');
        } catch (e: any) {
            setError(`Failed to generate example: ${e.message}`);
        }
    }, []);

    const clearAll = useCallback(() => {
        setEncodedJwt('');
        setHeader('');
        setPayload('');
        setSecret('');
        setIsJwtFormatValid(null);
        setIsSignatureValid(null);
        setVerificationResult(null);
        setError('');
    }, []);

    const switchMode = (newMode: JwtToolMode) => {
        setMode(newMode);
        // Trigger appropriate effect based on new mode
        if (newMode === 'decoder' && encodedJwt) {
            debouncedVerify(encodedJwt, secret);
        } else if (newMode === 'encoder' && header && payload) {
            const hObj = safeJsonParse(header);
            const pObj = safeJsonParse(payload);
            debouncedEncode(hObj, pObj, secret);
        }
    };

    return {
        mode,
        switchMode,
        encodedJwt,
        header,
        payload,
        secret,
        headerObj,
        payloadObj,
        isJwtFormatValid,
        isSignatureValid,
        verificationResult,
        error,
        handleEncodedJwtChange,
        handleHeaderChange,
        handlePayloadChange,
        handleSecretChange,
        generateExample,
        clearAll,
    };
};